import { FC, createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { jingqueAdminApi } from '@/api/jingqueAdminApi'

export interface PoolActivityOption {
  id: string
  name: string
  status: string
}

interface PoolActivityContextValue {
  poolActivities: PoolActivityOption[]
  currentPoolActivityId: string
  setCurrentPoolActivityId: (id: string) => void
  loading: boolean
  error: string
}

const STORAGE_KEY = 'jingque_admin_pool_activity_id'

const PoolActivityContext = createContext<PoolActivityContextValue>({
  poolActivities: [],
  currentPoolActivityId: '',
  setCurrentPoolActivityId: () => {},
  loading: false,
  error: '',
})

export const usePoolActivity = () => useContext(PoolActivityContext)

export const PoolActivityProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [poolActivities, setPoolActivities] = useState<PoolActivityOption[]>([])
  const [currentPoolActivityId, setCurrentPoolActivityIdState] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const load = async () => {
      try {
        const res = await jingqueAdminApi.activities.list(1, 100, { mode: 'pool' })
        const items: PoolActivityOption[] = (res.items || []).map(a => ({
          id: a.id,
          name: a.name,
          status: a.status,
        }))
        setPoolActivities(items)

        const saved = localStorage.getItem(STORAGE_KEY) || ''
        const validSaved = items.find(a => a.id === saved)
        if (validSaved) {
          setCurrentPoolActivityIdState(validSaved.id)
        } else if (items.length > 0) {
          setCurrentPoolActivityIdState(items[0].id)
          localStorage.setItem(STORAGE_KEY, items[0].id)
        }
      } catch (e: any) {
        setError(e.message || '加载池活动失败')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const setCurrentPoolActivityId = useCallback((id: string) => {
    setCurrentPoolActivityIdState(id)
    localStorage.setItem(STORAGE_KEY, id)
  }, [])

  const value = useMemo(() => ({
    poolActivities,
    currentPoolActivityId,
    setCurrentPoolActivityId,
    loading,
    error,
  }), [poolActivities, currentPoolActivityId, setCurrentPoolActivityId, loading, error])

  return (
    <PoolActivityContext.Provider value={value}>
      {children}
    </PoolActivityContext.Provider>
  )
}
